import React, { useState } from 'react';
import { Card, Button, Alert, Badge, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools, faCheckCircle, faExclamationTriangle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import apiClient from '../../services/api';

const ChatDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState(null);

  const runDiagnostics = async () => {
    setLoading(true);
    try {
      // Test básico de conectividad
      const healthResponse = await apiClient.get('/chat/health');
      const diagnosticsResponse = await apiClient.get('/chat/diagnostics');
      
      setDiagnostics({
        health: healthResponse.data,
        diagnostics: diagnosticsResponse.data,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setDiagnostics({
        error: error.message,
        timestamp: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  const testChatMessage = async () => {
    if (!testMessage.trim()) return;
    
    setLoading(true);
    try {
      const response = await apiClient.post('/chat/message', {
        message: testMessage
      });
      
      setTestResponse({
        success: true,
        data: response.data,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('Error testing chat message:', error);
      setTestResponse({
        success: false,
        error: error.message,
        response: error.response?.data,
        timestamp: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === true) return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} /> OK</Badge>;
    if (status === false) return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} /> Error</Badge>;
    return <Badge bg="warning"><FontAwesomeIcon icon={faExclamationTriangle} /> Desconocido</Badge>;
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <FontAwesomeIcon icon={faTools} className="me-2" />
        Diagnóstico del Chat
      </Card.Header>
      <Card.Body>
        <div className="d-flex gap-2 mb-3">
          <Button 
            onClick={runDiagnostics} 
            disabled={loading}
            variant="primary"
          >
            {loading ? 'Ejecutando...' : 'Ejecutar Diagnóstico'}
          </Button>
        </div>

        {diagnostics && (
          <div className="mb-4">
            <h6>Resultados del Diagnóstico</h6>
            {diagnostics.error ? (
              <Alert variant="danger">
                <strong>Error:</strong> {diagnostics.error}
              </Alert>
            ) : (
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Estado del Chat</span>
                  {getStatusBadge(diagnostics.health?.success)}
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Usuario puede usar chat</span>
                  {getStatusBadge(diagnostics.diagnostics?.data?.userCanUseChat)}
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Tiene OpenAI API Key</span>
                  {getStatusBadge(diagnostics.diagnostics?.data?.apiKeys?.hasOpenAIKey)}
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Chat habilitado</span>
                  {getStatusBadge(diagnostics.diagnostics?.data?.chatEnabled)}
                </ListGroup.Item>
              </ListGroup>
            )}
            
            {diagnostics.diagnostics && (
              <Alert variant="info" className="mt-3">
                <small>
                  <strong>Detalles técnicos:</strong><br />
                  <pre>{JSON.stringify(diagnostics.diagnostics.data, null, 2)}</pre>
                </small>
              </Alert>
            )}
          </div>
        )}

        <hr />

        <div>
          <h6>Test de Mensaje</h6>
          <div className="input-group mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Escribe un mensaje de prueba..."
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && testChatMessage()}
            />
            <Button 
              onClick={testChatMessage} 
              disabled={loading || !testMessage.trim()}
              variant="outline-primary"
            >
              Probar
            </Button>
          </div>

          {testResponse && (
            <Alert variant={testResponse.success ? "success" : "danger"}>
              <strong>Respuesta del Test:</strong>
              <pre className="mt-2 mb-0">
                {JSON.stringify(testResponse, null, 2)}
              </pre>
            </Alert>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ChatDiagnostics; 